import logger from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getUserId } from '../utils/utils';
import '../style/AdminStaffAvailability.css';

// Matches LAB_MACHINES in radiology-information-system-backend/routes/scheduling.js
// — RIS's real analyzer fleet (see lab_test_code_map / lab_instrument_results).
const MACHINE_OPTIONS = ['Piccolo', 'DxH520', 'AIA360', 'AIA900', 'McKesson120'];

const HOUR_OPTIONS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '17:40'];

const formatHourLabel = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  return `${displayHour}:${String(m).padStart(2, '0')} ${period}`;
};

const toLocalDateString = (year, month, day) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
const todayDateStr = () => {
  const d = new Date();
  return toLocalDateString(d.getFullYear(), d.getMonth(), d.getDate());
};

const ROLE_LABELS = {
  lab_tech: 'Lab Tech',
  medical_assistant: 'Medical Assistant',
  office_assistant: 'Office Assistant',
};

// Staff span three separate tables (lab_tech, medical_assistants, office_assistants) — a
// single numeric id isn't unique across them, so a composite "role:id" key is used
// everywhere on this page to identify the selected person.
const staffKey = (role, id) => `${role}:${id}`;

// Roles a brand-new hire can be flagged as not yet trained on labs — matches
// CANNOT_DO_LABS_ROLES in routes/scheduling.js. lab_tech is excluded: that
// role is assumed lab-capable from day one.
const CANNOT_DO_LABS_ROLES = new Set(['office_assistant', 'medical_assistant']);

// Rendered by components/StaffAvailability.js at /availability — moved out of the
// Admin Dashboard so lab staff can manage their own calendars without the admin role.
//
// It reads/writes the SAME lab_machine_assignments / lab_machine_staff_blocked_times
// / cannot_do_labs columns Health Hub's identical admin page uses — RIS and HH share one
// physical database (see docker-compose.yaml), so a change here is immediately visible in
// HH's admin page and vice versa. On the RIS side, a specimen type with no covering staff
// on a given day also hard-blocks booking via POST /scheduling/orderSchedule and shows a
// warning badge on the Schedule month calendar.
export default function AdminLabMachineAvailability({ BASE_URL, currentUser, user }) {
  const [staffList, setStaffList] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [machines, setMachines] = useState([]); // assigned machines for the selected staff member
  const [currentDate, setCurrentDate] = useState(new Date());
  const [blocks, setBlocks] = useState([]); // lab_machine_staff_blocked_times rows for the month

  const [showBlockModal, setShowBlockModal] = useState(false);
  const [pendingDate, setPendingDate] = useState(null);
  const [fullDay, setFullDay] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState('');

  const [coverageDate, setCoverageDate] = useState(todayDateStr());
  const [coverage, setCoverage] = useState(null);

  const selectedStaff = staffList.find(s => staffKey(s.staff_role, s.staff_id) === selectedKey) || null;

  // This page now lives outside the admin dashboard, so lab-capable staff can
  // reach it to manage their own calendar without being granted the admin role.
  // Admins keep full reach; everyone else is pinned to their own record. The
  // server enforces the same rule (canManageAvailabilityFor in
  // routes/scheduling.js) — this only stops the UI offering 403s.
  const isAdmin = currentUser?.type === 'admin';
  const selfId = getUserId(currentUser);
  const isSelf = (staff) => !!staff
    && staff.staff_role === currentUser?.type
    && String(staff.staff_id) === String(selfId);
  const canEditBlocks = isAdmin || isSelf(selectedStaff);
  // Machine sign-off and the "not yet trained" flag are competency records, not
  // scheduling preferences, so both stay admin-only — self-assigning a machine
  // would also be the coverage that unblocks booking that specimen type.
  const canEditMachines = isAdmin;

  const getMonthDateRange = (date) => {
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return {
      startDate: startOfMonth.toISOString().split('T')[0],
      endDate: endOfMonth.toISOString().split('T')[0],
    };
  };

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await fetch(`${BASE_URL}/scheduling/labMachineStaff`);
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        setStaffList(list);
        // Lab-capable staff land on their own calendar; admins on the first person.
        const own = list.find(s => s.staff_role === currentUser?.type
          && String(s.staff_id) === String(getUserId(currentUser)));
        if (own) setSelectedKey(staffKey(own.staff_role, own.staff_id));
        else if (list.length > 0) setSelectedKey(staffKey(list[0].staff_role, list[0].staff_id));
      } catch (err) {
        logger.error('Error fetching lab machine staff:', err);
      }
    };
    fetchStaff();
  }, []);

  const fetchMachines = async () => {
    if (!selectedStaff) return;
    try {
      const response = await fetch(`${BASE_URL}/scheduling/labMachineAssignments?staff_role=${selectedStaff.staff_role}&staff_id=${selectedStaff.staff_id}`);
      if (response.ok) {
        const data = await response.json();
        setMachines(data.map(m => m.machine));
      }
    } catch (err) {
      logger.error('Error fetching lab machine assignments:', err);
    }
  };

  const fetchBlocks = async () => {
    if (!selectedStaff) return;
    const { startDate, endDate } = getMonthDateRange(currentDate);
    try {
      const response = await fetch(
        `${BASE_URL}/scheduling/labMachineStaffBlockedTimes?staff_role=${selectedStaff.staff_role}&staff_id=${selectedStaff.staff_id}&startDate=${startDate}&endDate=${endDate}`
      );
      if (response.ok) {
        const data = await response.json();
        setBlocks(data);
      }
    } catch (err) {
      logger.error('Error fetching lab machine staff blocked times:', err);
    }
  };

  const fetchCoverage = async (date) => {
    try {
      const response = await fetch(`${BASE_URL}/scheduling/labMachineCoverage?date=${date}`);
      if (response.ok) {
        const data = await response.json();
        setCoverage(data);
      }
    } catch (err) {
      logger.error('Error fetching lab machine coverage:', err);
    }
  };

  useEffect(() => {
    fetchMachines();
  }, [selectedKey]);

  useEffect(() => {
    fetchBlocks();
  }, [currentDate, selectedKey]);

  useEffect(() => {
    fetchCoverage(coverageDate);
  }, [coverageDate]);

  const toggleMachine = async (machine) => {
    if (!selectedStaff || !canEditMachines) return;
    const isAssigned = machines.includes(machine);
    try {
      if (isAssigned) {
        const response = await fetch(
          `${BASE_URL}/scheduling/labMachineAssignments?staff_role=${selectedStaff.staff_role}&staff_id=${selectedStaff.staff_id}&machine=${encodeURIComponent(machine)}&user=${user}&type=${currentUser?.type}`,
          { method: 'DELETE' }
        );
        if (response.ok) {
          setMachines(prev => prev.filter(m => m !== machine));
          toast.success(`Removed ${machine}`);
        }
      } else {
        const response = await fetch(`${BASE_URL}/scheduling/labMachineAssignments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ staff_role: selectedStaff.staff_role, staff_id: selectedStaff.staff_id, machine, user, type: currentUser?.type }),
        });
        if (response.ok) {
          setMachines(prev => [...prev, machine]);
          toast.success(`Assigned ${machine}`);
        }
      }
      if (coverageDate) fetchCoverage(coverageDate);
    } catch (err) {
      logger.error('Error updating machine assignment:', err);
      toast.error('Error updating machine assignment');
    }
  };

  const toggleCannotDoLabs = async () => {
    if (!selectedStaff || !canEditMachines || !CANNOT_DO_LABS_ROLES.has(selectedStaff.staff_role)) return;
    const nextValue = !selectedStaff.cannot_do_labs;
    try {
      const response = await fetch(`${BASE_URL}/scheduling/labMachineStaffLabStatus`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ staff_role: selectedStaff.staff_role, staff_id: selectedStaff.staff_id, cannot_do_labs: nextValue, user, type: currentUser?.type }),
      });
      if (response.ok) {
        setStaffList(prev => prev.map(s =>
          staffKey(s.staff_role, s.staff_id) === selectedKey ? { ...s, cannot_do_labs: nextValue } : s
        ));
        toast.success(nextValue ? 'Marked cannot do labs yet' : 'Cleared — can do labs');
        fetchCoverage(coverageDate);
      } else {
        toast.error('Failed to update lab status');
      }
    } catch (err) {
      logger.error('Error updating cannot_do_labs:', err);
      toast.error('Error updating lab status');
    }
  };

  const handleDayClick = (day) => {
    if (!canEditBlocks) {
      toast.info('You can only change your own availability.');
      return;
    }
    const dateStr = toLocalDateString(currentDate.getFullYear(), currentDate.getMonth(), day);
    setPendingDate(dateStr);
    setFullDay(false);
    setStartTime('09:00');
    setEndTime('17:00');
    setReason('');
    setShowBlockModal(true);
  };

  const handleAddBlock = async () => {
    if (!selectedStaff) return;
    if (!fullDay && endTime <= startTime) {
      toast.error('End time must be after start time');
      return;
    }
    try {
      const response = await fetch(`${BASE_URL}/scheduling/labMachineStaffBlockedTimes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          staff_role: selectedStaff.staff_role,
          staff_id: selectedStaff.staff_id,
          blocked_date: pendingDate,
          full_day: fullDay,
          start_time: fullDay ? null : `${startTime}:00`,
          end_time: fullDay ? null : `${endTime}:00`,
          reason,
          user,
          type: currentUser?.type,
        }),
      });
      if (response.ok) {
        toast.success(`Blocked ${pendingDate}${fullDay ? ' (all day)' : ` from ${formatHourLabel(startTime)} to ${formatHourLabel(endTime)}`}`);
        setFullDay(false);
        setStartTime('09:00');
        setEndTime('17:00');
        setReason('');
        await fetchBlocks();
        if (pendingDate === coverageDate) fetchCoverage(coverageDate);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || 'Failed to block time');
      }
    } catch (err) {
      logger.error('Error blocking staff time:', err);
      toast.error('Error blocking staff time');
    }
  };

  const handleDeleteBlock = async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/scheduling/labMachineStaffBlockedTimes?id=${id}&user=${user}&type=${currentUser?.type}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        toast.success('Blocked time removed');
        await fetchBlocks();
        fetchCoverage(coverageDate);
      } else {
        toast.error('Failed to remove blocked time');
      }
    } catch (err) {
      logger.error('Error removing staff blocked time:', err);
      toast.error('Error removing blocked time');
    }
  };

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

  const days = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="avail-calendar-day" style={{ visibility: 'hidden' }} />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = toLocalDateString(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dayBlocks = blocks.filter(b => b.blocked_date.split('T')[0] === dateStr);
    const isFullyBlocked = dayBlocks.some(b => !!b.full_day);
    const hasPartialBlock = dayBlocks.some(b => !b.full_day);
    const isPast = new Date(dateStr) < new Date(new Date().toDateString());
    days.push(
      <div
        key={day}
        className={`avail-calendar-day ${isFullyBlocked ? 'full-blocked' : (hasPartialBlock ? 'partial-blocked' : '')} ${isPast ? 'past' : ''}`}
        onClick={() => handleDayClick(day)}
        title={isFullyBlocked
          ? 'Unavailable all day'
          : (hasPartialBlock ? 'Partially unavailable' : (canEditBlocks ? 'Click to add a block' : 'Available'))}
      >
        <span className="avail-day-number">{day}</span>
        {isFullyBlocked && <span className="avail-day-badge full">Unavailable</span>}
        {!isFullyBlocked && hasPartialBlock && <span className="avail-day-badge partial">Limited</span>}
      </div>
    );
  }

  const upcomingBlocks = blocks
    .filter(b => new Date(b.blocked_date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.blocked_date) - new Date(b.blocked_date) || a.start_time.localeCompare(b.start_time));

  const blocksForPendingDate = pendingDate ? blocks.filter(b => b.blocked_date.split('T')[0] === pendingDate) : [];

  return (
    <div className="avail-container">
      <h1 className="avail-title">Lab Machine Availability</h1>
      <p className="avail-subtitle">
        Track which of the clinic's lab machines each staff member can run — lab techs, medical assistants, and office
        assistants alike — and block off hourly windows or full days when someone is unavailable. Anyone listed under
        more than one role appears here only once. A specimen type with no covering staff on a given day is blocked
        from being scheduled and flagged on the Schedule calendar.
      </p>

      <div className="avail-selector-row">
        <label htmlFor="labstaff-select">Staff Member:</label>
        <select
          id="labstaff-select"
          className="avail-selector"
          value={selectedKey}
          onChange={(e) => setSelectedKey(e.target.value)}
        >
          {staffList.map(s => (
            <option key={staffKey(s.staff_role, s.staff_id)} value={staffKey(s.staff_role, s.staff_id)}>
              {s.first_name} {s.last_name} ({ROLE_LABELS[s.staff_role]}){s.cannot_do_labs ? ' — Cannot Do Labs' : ''}
            </option>
          ))}
        </select>
        {selectedStaff && canEditMachines && CANNOT_DO_LABS_ROLES.has(selectedStaff.staff_role) && (
          <button
            type="button"
            className={selectedStaff.cannot_do_labs ? 'avail-btn-confirm' : 'avail-btn-cancel'}
            onClick={toggleCannotDoLabs}
          >
            {selectedStaff.cannot_do_labs ? 'Clear — Trained on Labs Now' : 'Mark Cannot Do Labs (New Hire)'}
          </button>
        )}
      </div>

      <div className="avail-modality-section">
        <h3>Machines This Person Can Run</h3>
        {selectedStaff?.cannot_do_labs ? (
          <p className="avail-subtitle">
            This {ROLE_LABELS[selectedStaff.staff_role]} is flagged as not yet trained on lab machines.
            {canEditMachines
              ? ' Clear the flag above before assigning machines.'
              : ' An admin must clear the flag before machines can be assigned.'}
          </p>
        ) : (
          <>
            {!canEditMachines && (
              <p className="avail-subtitle">
                Machine sign-off is a training record — only an admin can change it.
              </p>
            )}
            <div className="avail-modality-checkboxes">
              {MACHINE_OPTIONS.map(machine => (
                <label key={machine} className="avail-modality-checkbox">
                  <input
                    type="checkbox"
                    checked={machines.includes(machine)}
                    disabled={!canEditMachines}
                    onChange={() => toggleMachine(machine)}
                  />
                  {machine}
                </label>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="avail-two-col">
        <div className="avail-coverage-section">
          <h3>Daily Machine Coverage</h3>
          <input
            type="date"
            className="avail-coverage-date-input"
            value={coverageDate}
            onChange={(e) => setCoverageDate(e.target.value)}
          />
          {coverage && (
            <table className="avail-coverage-table">
              <thead>
                <tr>
                  <th>Machine</th>
                  <th>Staff</th>
                </tr>
              </thead>
              <tbody>
                {MACHINE_OPTIONS.map(machine => {
                  const staffRows = coverage.machines?.[machine] || [];
                  return (
                    <tr key={machine}>
                      <td>{machine}</td>
                      <td>
                        {staffRows.length === 0 && <span style={{ color: '#999' }}>No staff assigned</span>}
                        {staffRows.map(s => {
                          const status = s.fully_blocked ? 'unavailable' : (s.blocked_windows.length > 0 ? 'partial' : 'available');
                          const label = status === 'unavailable'
                            ? `${s.name} — Unavailable`
                            : status === 'partial'
                              ? `${s.name} — ${s.blocked_windows.map(w => `${formatHourLabel(w.start.slice(0, 5))}–${formatHourLabel(w.end.slice(0, 5))}`).join(', ')}`
                              : `${s.name} — Available`;
                          return (
                            <span key={staffKey(s.staff_role, s.staff_id)} className={`avail-tech-chip ${status}`}>{label}</span>
                          );
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {upcomingBlocks.length > 0 && (
          <div className="avail-upcoming-section">
            <h3>Upcoming Blocked Times</h3>
            <ul className="avail-upcoming-list">
              {upcomingBlocks.map(b => {
                const [year, month, day] = b.blocked_date.split('T')[0].split('-');
                const display = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10))
                  .toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                return (
                  <li key={b.id} className={`avail-upcoming-item ${b.full_day ? '' : 'partial'}`}>
                    <span className="avail-upcoming-date">
                      {display}{!b.full_day && ` — ${formatHourLabel(b.start_time.slice(0, 5))}–${formatHourLabel(b.end_time.slice(0, 5))}`}
                      {!!b.full_day && ' — All Day'}
                    </span>
                    {b.reason && <span className="avail-upcoming-reason">{b.reason}</span>}
                    {canEditBlocks && (
                      <button className="avail-remove-btn" onClick={() => handleDeleteBlock(b.id)}>Remove</button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <div className="avail-calendar-header">
        <button className="avail-nav-btn" onClick={prevMonth}>{'<'}</button>
        <h2>{currentDate.toLocaleString('default', { month: 'long' })} {currentDate.getFullYear()}</h2>
        <button className="avail-nav-btn" onClick={nextMonth}>{'>'}</button>
      </div>

      <div className="avail-calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="avail-calendar-day-header">{d}</div>
        ))}
        {days}
      </div>

      {showBlockModal && (
        <div className="avail-modal-overlay" onClick={() => setShowBlockModal(false)}>
          <div className="avail-modal-box" onClick={e => e.stopPropagation()}>
            <h3>Block {pendingDate}</h3>

            {blocksForPendingDate.length > 0 && (
              <div className="avail-existing-blocks">
                <div className="avail-existing-blocks-title">Existing blocks for this day</div>
                {blocksForPendingDate.map(b => (
                  <div key={b.id} className="avail-existing-block-item">
                    <span>
                      {b.full_day ? 'All Day' : `${formatHourLabel(b.start_time.slice(0, 5))}–${formatHourLabel(b.end_time.slice(0, 5))}`}
                      {b.reason ? ` (${b.reason})` : ''}
                    </span>
                    <button className="avail-remove-btn" onClick={() => handleDeleteBlock(b.id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}

            <label className="avail-form-checkbox-label">
              <input type="checkbox" checked={fullDay} onChange={(e) => setFullDay(e.target.checked)} />
              Unavailable all day
            </label>

            {!fullDay && (
              <div className="avail-time-row">
                <label className="avail-form-label">
                  Start Time
                  <select className="avail-select" value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                    {HOUR_OPTIONS.map(h => <option key={h} value={h}>{formatHourLabel(h)}</option>)}
                  </select>
                </label>
                <label className="avail-form-label">
                  End Time
                  <select className="avail-select" value={endTime} onChange={(e) => setEndTime(e.target.value)}>
                    {HOUR_OPTIONS.map(h => <option key={h} value={h}>{formatHourLabel(h)}</option>)}
                  </select>
                </label>
              </div>
            )}

            <label className="avail-form-label">
              Reason (optional)
              <input
                type="text"
                className="avail-reason-input"
                placeholder="e.g. Training, PTO..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>

            <div className="avail-modal-buttons">
              <button className="avail-btn-confirm" onClick={handleAddBlock}>Add Block</button>
              <button className="avail-btn-cancel" onClick={() => setShowBlockModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
